import React, { useState } from 'react';
import { Minus, Plus, X } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { formatCurrency } from '../../utils/formatters';

const FALLBACK_IMAGE = '/images/wreaths/placeholder.svg';
const MAX_QUANTITY = 99;

const CartItem = ({ item, onRequestRemove }) => {
  const { updateQuantity, getLineKey } = useCart();
  const [imgError, setImgError] = useState(false);

  const lineKey = getLineKey(item);
  const atMin = item.quantity <= 1;
  const atMax = item.quantity >= MAX_QUANTITY;

  const handleDecrease = () => {
    if (!atMin) {
      updateQuantity(lineKey, item.quantity - 1);
    }
  };

  const handleIncrease = () => {
    if (!atMax) {
      updateQuantity(lineKey, item.quantity + 1);
    }
  };

  // 수량 직접 입력
  const handleInputChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw === '') {
      updateQuantity(lineKey, 1);
      return;
    }
    let next = parseInt(raw, 10);
    if (Number.isNaN(next) || next < 1) next = 1;
    if (next > MAX_QUANTITY) next = MAX_QUANTITY;
    updateQuantity(lineKey, next);
  };

  const handleRemove = () => {
    if (onRequestRemove) {
      onRequestRemove(item);
    }
  };

  const subtotal = item.price * item.quantity;
  const ribbon = item.ribbon;
  // 장바구니 담은 이후 품절된 상품 표시 (inStock 이 명시적으로 false 인 경우)
  const isSoldOut = item.inStock === false;

  return (
    <div
      className={`flex items-start gap-4 p-4 bg-white border rounded-lg ${
        isSoldOut ? 'border-red-200 bg-red-50/40' : 'border-accent-200'
      }`}
    >
      {/* 화환 썸네일 */}
      <div className="relative flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden bg-accent-100">
        <img
          src={imgError ? FALLBACK_IMAGE : item.image}
          alt={item.name}
          loading="lazy"
          onError={() => setImgError(true)}
          className={`w-full h-full object-cover ${isSoldOut ? 'opacity-50' : ''}`}
        />
        {isSoldOut && (
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            <span className="text-white text-xs font-bold">품절</span>
          </div>
        )}
      </div>

      {/* 상품 정보 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-base md:text-lg font-bold text-primary truncate">
            {item.name}
          </h3>
          {isSoldOut && (
            <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
              품절
            </span>
          )}
        </div>

        <p className="text-sm text-accent-600 mb-2">{item.category}</p>

        {isSoldOut && (
          <p className="text-xs text-red-600 mb-2">
            현재 품절된 상품입니다. 주문 전 삭제해주세요.
          </p>
        )}

        {/* 근조 리본 문구 요약 */}
        {ribbon && (ribbon.left || ribbon.right || ribbon.sender) && (
          <div className="mb-2 text-xs text-accent-700 bg-accent-50 rounded-md p-2 border border-accent-200">
            <p className="font-medium text-primary mb-0.5">근조 리본</p>
            {ribbon.left && <p className="truncate">좌: {ribbon.left}</p>}
            {ribbon.right && <p className="truncate">우: {ribbon.right}</p>}
            {ribbon.sender && <p className="truncate">보내는 분: {ribbon.sender}</p>}
          </div>
        )}

        {/* 가격 정보 (모바일) */}
        <div className="md:hidden space-y-1">
          <p className="text-sm text-accent-600">단가: {formatCurrency(item.price)}</p>
          <p className="text-base font-bold text-primary">소계: {formatCurrency(subtotal)}</p>
        </div>
      </div>

      {/* 수량 조절 */}
      <div className="flex items-center gap-1.5">
        <button
          onClick={handleDecrease}
          disabled={atMin}
          className="w-8 h-8 flex items-center justify-center rounded-md border border-accent-300 hover:bg-accent-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          aria-label="수량 감소"
        >
          <Minus size={16} className="text-primary" />
        </button>

        <input
          type="text"
          inputMode="numeric"
          value={item.quantity}
          onChange={handleInputChange}
          aria-label="수량"
          className="w-10 h-8 text-center font-medium text-primary border border-accent-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        />

        <button
          onClick={handleIncrease}
          disabled={atMax}
          className="w-8 h-8 flex items-center justify-center rounded-md border border-accent-300 hover:bg-accent-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          aria-label="수량 증가"
        >
          <Plus size={16} className="text-primary" />
        </button>
      </div>

      {/* 가격 정보 (데스크톱) */}
      <div className="hidden md:flex md:flex-col md:items-end md:min-w-[120px]">
        <p className="text-sm text-accent-600 mb-1">{formatCurrency(item.price)}</p>
        <p className="text-lg font-bold text-primary">{formatCurrency(subtotal)}</p>
      </div>

      {/* 삭제 버튼 */}
      <button
        onClick={handleRemove}
        className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors group"
        aria-label="장바구니에서 제거"
      >
        <X size={20} className="text-accent-500 group-hover:text-red-600 transition-colors" />
      </button>
    </div>
  );
};

export default CartItem;
